import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';

import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import { IS_SHORT_VIEWPORT } from '../../utils/screensize';

interface ScheduleSearchBarProps {
  onChangeText: (text: string) => void;
  onFilterPress?: () => void;
  hasActiveFilter?: boolean;
}

const DEBOUNCE_MS = 300;

const ScheduleSearchBar: React.FC<ScheduleSearchBarProps> = ({ onChangeText, onFilterPress, hasActiveFilter }) => {
  const { t } = useTranslation('home');
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (text: string) => {
      setLocalValue(text);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChangeText(text);
      }, DEBOUNCE_MS);
    },
    [onChangeText]
  );

  return (
    <View style={styles.container}>
      <View style={[styles.searchBox, isFocused ? styles.searchBoxFocused : null]}>
        <Icon name="magnify" size={20} color={isFocused ? colors.primary : '#A7AFBA'} style={styles.searchIcon} />
        <TextInput
          style={styles.input}
          placeholder={t('newHome.searchPlaceholder')}
          placeholderTextColor="#B2B8C2"
          value={localValue}
          onChangeText={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          returnKeyType="search"
        />
      </View>
      {onFilterPress && (
        <TouchableOpacity
          style={[
            styles.filterButton,
            isFocused ? styles.filterButtonFocused : null,
            hasActiveFilter ? styles.filterButtonActive : null
          ]}
          onPress={onFilterPress}
          activeOpacity={0.7}
        >
          <Icon name="tune-vertical" size={22} color={hasActiveFilter ? colors.primary : colors.darkgray} />
          {hasActiveFilter && <View style={styles.filterActiveDot} />}
        </TouchableOpacity>
      )}
    </View>
  );
};

export default React.memo(ScheduleSearchBar);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: IS_SHORT_VIEWPORT ? 10 : 20
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#D6DCE5',
    paddingHorizontal: 14,
    height: 48,
    shadowColor: '#0F172A',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2
  },
  searchBoxFocused: {
    borderColor: colors.primary,
    backgroundColor: '#FCFFFD',
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 4
  },
  searchIcon: {
    marginRight: 8
  },
  input: {
    flex: 1,
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 14,
    color: '#25282B',
    padding: 0
  },
  filterButton: {
    marginLeft: 10,
    width: 48,
    height: 48,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#D6DCE5',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterButtonFocused: {
    borderColor: colors.primary,
    backgroundColor: '#FCFFFD'
  },
  filterButtonActive: {
    borderColor: colors.primary,
    backgroundColor: '#F0FDF4'
  },
  filterActiveDot: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary
  }
});
